import React, {useEffect, useState} from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import colors from '../../../configuration/colors'
import { hideModal } from '../../../actions/modal'
import Modal from '../../mini/Modal'
import SelectorComponent from './InputComponents/SelectorComponent'
import TextInputComponent from './InputComponents/TextInputComponent'
import Button from '../../mini/Buttons/Button'
import CancelButton from '../../mini/Buttons/CancelButton'
import H2 from '../../typography/headers/H2'
import MarginRight15 from '../../typography/margins/MarginRight15'
import {cloneMasterCourse, createNewCourse, editMentorActivityCourse, publishJourney} from '../../mycourses/API'
import withApiData from '../../HOC/withApiData'
import {cleanColor, cleanHexCode} from '../../mycourses/util'
import { PopoverPicker } from '../../colorpicker/PopoverPicker'
import {push} from 'react-router-redux'

const ModalContainer = styled.div`
  width: 660px;
  margin: 100px auto;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.08);
  background-color: ${colors.smoke};
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const CreateNewCourseModal = ({apiData, frameworkSubjects, gradeLevels, selectedAcademicYearId, selectedCourse}) => {

  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [isMutating, setIsMutating] = useState(false)
  const [isError, setIsError] = useState(null)

  const [selectedCourseLevel, setSelectedCourseLevel] = useState(null)
  const handleSelectedCourseLevel = e => {
    setSelectedCourseLevel(e)
  }

  const [selectedFrameworkSubject, setSelectedFrameworkSubject] = useState(null)
  const handleSelectedFrameworkSubject = e => {
    setSelectedFrameworkSubject(e)
  }

  const [courseName, setCourseName] = useState('')
  const handleCourseName = e => {
    setCourseName(e.target.value)
  }

  const [abbreviation, setAbbreviation] = useState('')
  const handleAbbreviation = e => {
    setAbbreviation(e.target.value)
  }

  const [courseColor, setCourseColor] = useState('')

  useEffect(() => {
    // Prefill with the copied course if user chose to create a course from an existing one (only carrying over 'Course name' preceded with 'copy of' and 'Abbreviation'
    if (selectedCourse) {
      // const currentCourseLevel = gradeLevels.filter((level) => level.id === selectedCourse.grade_level_id)
      // setSelectedCourseLevel(currentCourseLevel[0])
      //
      // const currentFrameworkSubject = frameworkSubjects.filter(
      //   (subject) => subject.id === selectedCourse.framework_subject_id
      // )
      // setSelectedFrameworkSubject(currentFrameworkSubject[0])

      setCourseName('Copy of ' + selectedCourse.name)

      setAbbreviation(selectedCourse.abbreviation)

      // setCourseColor('#' + selectedCourse.color)
    }
  }, [])

  const isValidColor = colorName => {
    return colorName.match(/^#[a-f0-9]{6}$/i) !== null
  }

  const handleCloseModal = () => {
    dispatch(hideModal())
  }

  const couldSaveData =
    selectedCourseLevel !== null &&
    selectedFrameworkSubject !== null &&
    courseName.length > 0 &&
    abbreviation.length > 0 &&
    abbreviation.length <= 3 &&
    isValidColor(courseColor === '' ? '#4E6C83' : courseColor)

  const createMutation = useMutation(
    ({
       selectedCourseLevel,
       selectedFrameworkSubject,
       courseName,
       abbreviation,
       courseColor,
       selectedAcademicYearId
     }) =>
      createNewCourse(
        apiData,
        selectedCourseLevel,
        selectedFrameworkSubject,
        courseName,
        abbreviation,
        courseColor ? cleanColor(courseColor) : '4E6C84',
        selectedAcademicYearId
      )
  )
  const cloneMutation = useMutation(
    ({ apiData, masterCourseId }) =>
      cloneMasterCourse(
        apiData,
        masterCourseId,
        selectedAcademicYearId,
        courseName,
        abbreviation,
        courseColor ? cleanColor(courseColor) : '4E6C84',
        selectedFrameworkSubject.id,
        selectedCourseLevel.id
      )
  )
  const editMutation = useMutation(
    ({ courseName, abbreviation, courseColor, selectedAcademicYearId, activityCourse }) =>
      editMentorActivityCourse(
        apiData,
        courseName,
        abbreviation,
        courseColor,
        activityCourse,
        selectedAcademicYearId
      )
  )
  const handleSaveCourse = () => {
    if (couldSaveData && selectedCourse) {
      setIsError(null)
      setIsMutating(true)
      cloneMutation.mutate({
        apiData: apiData,
        masterCourseId: selectedCourse.id,
        academicYearId: selectedAcademicYearId,
        courseName: courseName,
        abbreviation: abbreviation ? abbreviation : selectedCourse.abbreviation,
        color: courseColor === '' ? '4E6C84' : cleanColor(courseColor),
        frameworkSubjectId: selectedFrameworkSubject
          ? selectedFrameworkSubject.id
          : selectedCourse.framework_subject_id,
        gradeLevelId: selectedCourseLevel
          ? selectedCourseLevel.id
          : selectedCourse.grade_level_id
      },
        {
          onSuccess: (data) => {
            editMutation.mutate(
              {
                courseName,
                abbreviation,
                courseColor: courseColor === '' ? '4E6C84' : cleanColor(courseColor),
                selectedAcademicYearId,
                activityCourse: data
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(
                    'mentor-master-courses-' + selectedAcademicYearId
                  )
                  setTimeout(() => {
                    setIsMutating(false)
                    handleCloseModal()
                  }, 300)
                }
              }
            )
            setIsMutating(false)
            dispatch(hideModal())
            queryClient.invalidateQueries(`mentor-master-courses-${selectedAcademicYearId}`)
          },
          onError: (error) => {
            setIsMutating(false)
            setIsError(error)
            console.log(error)
            dispatch(hideModal())
            dispatch(push('/error'))
          }
        })
    } else if (couldSaveData && !selectedCourse) {
      setIsMutating(true)
      setIsError(null)
      createMutation.mutate(
        {
          selectedCourseLevel,
          selectedFrameworkSubject,
          courseName,
          abbreviation,
          courseColor: courseColor === '' ? '4E6C84' : cleanColor(courseColor),
          selectedAcademicYearId
        },
        {
          onSuccess: data => {
            if (data.academic_year_id === selectedAcademicYearId) {
              queryClient.setQueryData(
                ['mentor-master-courses-' + selectedAcademicYearId],
                courses => {
                  const newCourses = [...courses]
                  newCourses.push({ ...data, ...{ just_created: true } })
                  return newCourses
                }
              )
            }

            setTimeout(() => {
              setIsMutating(false)
              handleCloseModal()
            }, 300)
          },
          onError: (error) => {
            setIsMutating(false)
            setIsError(error)
            console.log(error)
            dispatch(hideModal())
            dispatch(push('/error'))
          }
        }
      )
    }
  }
  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          <H2>+ Create Course</H2>
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={handleCloseModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="createCourseTooltip"
              data-tip="Please enter all required fields prior to creating this course."
            >
              <Button
                width="104px"
                title="Create"
                handleClick={handleSaveCourse}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                isMutating={isMutating}
                couldSaveData={couldSaveData}
                shouldShowTooltipAndDisableButton={true}
              />
              {!couldSaveData && (
                <ReactTooltip
                  id="createCourseTooltip"
                  place="bottom"
                  effect="solid"
                  className="activityCourseTooltip"
                />
              )}
            </div>
          </FlexContainer>
        </HeaderContainer>
        <MainContainer>
          <SelectorComponent
            placeholder="Course Level"
            handleSelectedValueChange={handleSelectedCourseLevel}
            selectedValue={selectedCourseLevel}
            options={gradeLevels.filter(
              gl => gl.available_for_activity_courses === true
            )}
            shouldDisplayAsterisk={true}
          />
          <SelectorComponent
            placeholder="Framework Subject"
            handleSelectedValueChange={handleSelectedFrameworkSubject}
            selectedValue={selectedFrameworkSubject}
            options={frameworkSubjects.filter(
              fS => fS.framework_type === 'activity_courses'
            )}
            shouldDisplayAsterisk={true}
          />
          <TextInputComponent
            title="Course Name"
            placeholder="Course Name"
            hintText="Example: Spanish 2, Art 1, Psychology"
            handleEnteredTextValue={handleCourseName}
            value={courseName}
            shouldDisplayAsteriskSymbol={true}
          />
          <TextInputComponent
            title="Abbreviation"
            placeholder="Abbreviation"
            hintText="Must be no more than 3 letters. Example: Sp1, Psy, Ar1"
            handleEnteredTextValue={handleAbbreviation}
            value={abbreviation}
            shouldDisplayAsteriskSymbol={true}
          />
          <FlexContainer>
            <TextInputComponent
              title="Course Color Hex Code"
              placeholder="Course Color Hex Code"
              hintText="If left empty, #4e6c84 will be applied."
              // cannot choose a hex code to respect color accessibility.
              // handleEnteredTextValue={handleCourseColor}
              value={courseColor}
              shouldDisplayAsteriskSymbol={false}
            />
            <PopoverPicker color={courseColor === '' ? '#4e6c84' : cleanHexCode(courseColor)} onChange={setCourseColor} />
          </FlexContainer>
        </MainContainer>
      </ModalContainer>
    </Modal>
  )
}

CreateNewCourseModal.propTypes = {
  closeCallback: PropTypes.func,
  activity: PropTypes.object,
  resource: PropTypes.object,
  quest: PropTypes.object,
  activities: PropTypes.array,
  selectedAcademicYearId: PropTypes.number.isRequired
}

export default withApiData(CreateNewCourseModal)



// WEBPACK FOOTER //
// ./src/components/modal/CreateOrEditCourseModal/CreateNewCourseModal.js